import { Field, FormikProps } from 'formik';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import * as uuid from 'uuid';

import {
  RadioButton,
  Select,
  TextField,
  Typography,
} from '@vivaahealth/design-system';

import { parseValue } from 'common/dist/utils/types';

import { SubscriberUpload } from './schema';
import styles from './subscriber.module.css';

import * as api from '../../../../../api';
import ContextualHelpModal from '../../../../../components/ContextualHelpModal';
import InfoLabel from '../../../../../components/ContextualHelpModal/InfoLabel';
import LabeledInput from '../../../../../components/LabeledInput';
import { DatePicker } from '../../../../../components/DatePicker';
import MailingAddressInput from '../../../../../components/Guarantor/MailingAddressInput';
import { getValueFromFieldOrPatient } from '../../../../../components/utils';
import { convertPlaceToAddress } from '../../../../../components/AddressInput/utils';
import * as store from '../../../../../reducers/store';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../../utils/formSet';
import { EMPLOYMENT_STATUS_SHOW_EMPLOYER } from 'common/dist/utils/previsitFormField';

const SUBSCRIBER_HELP_TEXT = `## Who is my subscriber?

The subscriber is the person who pays for health insurance premiums or whose employment is the basis for membership in the insurance plan.`;

interface StateProps {
  forms: store.Forms;
  patient: store.Loaded['loggedInPatient'];
  patientRelationshipsToSubscriber: store.Loaded['masterPracticePreference']['patientRelationshipsToSubscriber'];
}

interface SelectSubscriberFormProps extends StateProps, WithTranslation {
  formikProps: FormikProps<SubscriberUpload>;
  mergedSubscribers: api.PrevisitPatientInsurance.PrevisitSubscriber[];
  employmentStatuses: api.PracticePreference.EmploymentStatus[];
  employers: (typeof api.Employer)[];
  newSubscriber: boolean;
  setNewSubscriber: (newSubscriber: boolean) => void;
}

const SelectSubscriberForm = ({
  employers,
  formikProps,
  forms,
  mergedSubscribers,
  patient,
  employmentStatuses,
  patientRelationshipsToSubscriber,
  newSubscriber,
  setNewSubscriber,
  t,
}: SelectSubscriberFormProps) => {
  const [showSubscriberHelp, setShowSubscriberHelp] = useState(false);

  const getPatientAddress = useCallback(() => {
    const fields = _.flatten(Object.values(forms).map((form) => form.fields));
    const patientPlaceDetails = parseValue<api.Places.PlaceDetails>(
      getValueFromFieldOrPatient(
        api.PrevisitFormTemplateFieldConfig.Primitive.PatientAddressMailing,
        fields,
        patient,
      ) || '',
    );
    return patientPlaceDetails && convertPlaceToAddress(patientPlaceDetails);
  }, [forms, patient]);

  return (
    <div className={styles['form-container']}>
      <LabeledInput label={t('whoIsPolicyholderQuestion')}>
        <div className={styles['radio-button-group']}>
          {mergedSubscribers.map((subscriber) => (
            <RadioButton
              checked={formikProps.values.subscriberId === subscriber.id}
              key={subscriber.id}
              label={
                [
                  subscriber.primaryPolicyHolderFirstName,
                  subscriber.primaryPolicyHolderLastName,
                ]
                  .filter(Boolean)
                  .join(' ') || t('self')
              }
              name="subscriberId"
              onClick={() => {
                setNewSubscriber(false);
                formikProps.setFieldValue('subscriberId', subscriber.id);
              }}
            />
          ))}
          <RadioButton
            label={t('someoneElse')}
            name="subscriberId"
            onClick={() => {
              setNewSubscriber(true);
              formikProps.setFieldValue('subscriberId', uuid.v4());
            }}
          />
        </div>
        <>
          <ContextualHelpModal
            headerText={t('moreInformation')}
            helpText={t(SUBSCRIBER_HELP_TEXT)}
            isOpen={showSubscriberHelp}
            onRequestClose={() => setShowSubscriberHelp(false)}
            onHandleHelpful={async () => setShowSubscriberHelp(false)}
          />
          <InfoLabel
            className={styles['info-label']}
            onClick={() => setShowSubscriberHelp(true)}
            label={t('notSureQuestion')}
          />
        </>
      </LabeledInput>
      {newSubscriber && (
        <>
          <Field
            as={TextField}
            name={'primaryPolicyHolderFirstName'}
            label={t('firstName')}
            error={
              formikProps.touched.primaryPolicyHolderFirstName &&
              formikProps.errors.primaryPolicyHolderFirstName
            }
          />
          <Field
            as={TextField}
            name={'primaryPolicyHolderLastName'}
            label={t('lastName')}
            error={
              formikProps.touched.primaryPolicyHolderLastName &&
              formikProps.errors.primaryPolicyHolderLastName
            }
          />
          <LabeledInput label={t('birthDate')}>
            <DatePicker
              curvedInput={5}
              date={
                formikProps.values.primaryPolicyHolderDob &&
                !isNaN(
                  new Date(formikProps.values.primaryPolicyHolderDob).valueOf(),
                )
                  ? new Date(
                      formikProps.values.primaryPolicyHolderDob,
                    ).toISOString()
                  : ''
              }
              onChange={(birthday) => {
                formikProps.setFieldValue('primaryPolicyHolderDob', birthday);
              }}
            />
          </LabeledInput>
          <LabeledInput label={t('sex')} htmlFor="subscriber-sex">
            <div className={styles['radio-button-group']}>
              {api.Previsit.DefaultSexes.map((sex) => (
                <RadioButton
                  key={sex}
                  label={t(api.Previsit.sexOptionsMap[sex])}
                  name="subscriber-sex"
                  onClick={() => {
                    formikProps.setFieldValue('primaryPolicyHolderSex', sex);
                  }}
                />
              ))}
            </div>
          </LabeledInput>
          <MailingAddressInput
            label={t('mailingAddress')}
            onChange={(address) =>
              formikProps.setFieldValue('primaryPolicyHolderAddress', address)
            }
            value={formikProps.values.primaryPolicyHolderAddress ?? null}
            patientAddress={getPatientAddress()}
          />
          <Field
            as={TextField}
            name={'primaryPolicyHolderHomePhone'}
            label={t('mobilePhoneNumberOptional')}
            error={formikProps.errors.primaryPolicyHolderHomePhone}
          />
          {employmentStatuses.length > 0 && (
            <LabeledInput
              label={t('employmentStatus')}
              htmlFor="employment-status"
            >
              <div className={styles['radio-button-group']}>
                {employmentStatuses.map((status) => (
                  <RadioButton
                    key={status}
                    label={t(api.Previsit.employmentStatusOptionsMap[status])}
                    name="employment-status"
                    onClick={() => {
                      formikProps.setFieldValue(
                        'primaryPolicyHolderEmploymentStatus',
                        status,
                      );
                    }}
                  />
                ))}
              </div>
            </LabeledInput>
          )}
          {formikProps.values.primaryPolicyHolderEmploymentStatus &&
            EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(
              formikProps.values.primaryPolicyHolderEmploymentStatus,
            ) &&
            employers.length > 0 && (
              <Select
                name="primaryPolicyHolderEmployer"
                label={t('primaryPolicyHolderEmployer')}
                value={{
                  label: formikProps.values.primaryPolicyHolderEmployer ?? null,
                  value: formikProps.values.primaryPolicyHolderEmployer ?? null,
                }}
                error={
                  !!formikProps.touched.primaryPolicyHolderEmployer
                    ? formikProps.errors.primaryPolicyHolderEmployer
                    : undefined
                }
                onBlur={() =>
                  formikProps.setFieldTouched(
                    'primaryPolicyHolderEmployer',
                    true,
                    false,
                  )
                }
                onChange={({ value }: any) => {
                  formikProps.setFieldValue(
                    'primaryPolicyHolderEmployer',
                    value,
                  );
                }}
                options={employers.map((employer) => ({
                  label: employer,
                  value: employer,
                }))}
              />
            )}
          <div className={styles['header-wrapper']}>
            <Typography variant="headline-small" bold>
              {t('patientRelationshipToPrimaryPolicyholderTitle')}
            </Typography>
          </div>
          <Select
            className={styles.select}
            name="patientRelationshipToPrimaryPolicyholder"
            label={t(
              patientRelationshipsToSubscriber.isReverseRelationship
                ? 'primaryPolicyHolderRelationshipToPatient'
                : 'patientRelationshipToPrimaryPolicyholder',
            )}
            value={{
              label: !!formikProps.values
                .patientRelationshipToPrimaryPolicyholder
                ? t(
                    api.Previsit.relationshipOptionsMap[
                      formikProps.values
                        .patientRelationshipToPrimaryPolicyholder
                    ],
                  )
                : null,
              value:
                formikProps.values.patientRelationshipToPrimaryPolicyholder ||
                null,
            }}
            error={
              !!formikProps.touched.patientRelationshipToPrimaryPolicyholder
                ? formikProps.errors.patientRelationshipToPrimaryPolicyholder
                : undefined
            }
            onBlur={() =>
              formikProps.setFieldTouched(
                'patientRelationshipToPrimaryPolicyholder',
                true,
                false,
              )
            }
            onChange={({ value: newRelationship }: any) => {
              formikProps.setFieldValue(
                'patientRelationshipToPrimaryPolicyholder',
                newRelationship,
              );
            }}
            options={patientRelationshipsToSubscriber.relationships
              .filter(
                (relationship) => relationship !== api.Common.Relationship.Self,
              )
              .map((relationship: api.Common.Relationship) => ({
                label: t(api.Previsit.relationshipOptionsMap[relationship]),
                value: relationship,
              }))}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPractice = getSelectedPracticePreference(state);

  return {
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
    patient: state.loggedInPatient,
    patientRelationshipsToSubscriber:
      selectedPractice.patientRelationshipsToSubscriber,
  };
};

export default connect(mapStateToProps)(
  withTranslation('subscriberPage')(SelectSubscriberForm),
);
